<template>
  <b-modal v-model="visible" size="xl" centered hide-footer no-close-on-backdrop :no-close-on-esc="sending"
           body-class="p-0" content-class="wc-modal" @hidden="reset">
    <template #modal-title>
      <span class="wc-title"><i class="fab fa-whatsapp"/>{{ $t('whatsappHub.composer.title') }}
        <span class="wc-title__count tabular">{{ recipients.length }}</span>
      </span>
    </template>

    <!-- ═══ results view ═══ -->
    <div v-if="results" class="wc-results">
      <div class="wc-results__head">
        <span class="wc-results__ok"><i class="fas fa-circle-check"/>{{ okCount }} {{ $t('whatsappHub.composer.resultOk') }}</span>
        <span v-if="failCount" class="wc-results__fail"><i class="fas fa-circle-xmark"/>{{ failCount }} {{ $t('whatsappHub.composer.resultFail') }}</span>
        <span v-if="scheduledCount" class="wc-results__sched"><i class="far fa-clock"/>{{ scheduledCount }} {{ $t('whatsappHub.status.scheduled') }}</span>
      </div>
      <div class="wc-results__list">
        <div v-for="r in results" :key="r.clientId" class="wc-result" :class="r.success ? 'wc-result--ok' : 'wc-result--fail'">
          <i :class="r.success ? (r.scheduled ? 'far fa-clock' : 'fas fa-check') : 'fas fa-triangle-exclamation'"/>
          <span class="wc-result__name">{{ r.name || ('#' + r.clientId) }}</span>
          <span class="wc-result__phone tabular">{{ r.phone }}</span>
          <span class="wc-result__text">{{ r.success ? (r.renderedBody || '') : r.error }}</span>
        </div>
      </div>
      <div class="wc-foot">
        <b-button variant="secondary" @click="close">{{ $t('whatsappHub.composer.close') }}</b-button>
      </div>
    </div>

    <!-- ═══ compose view ═══ -->
    <div v-else class="wc">
      <div class="wc-main">

        <!-- recipients -->
        <section class="wc-block">
          <h6 class="wc-block__title">{{ $t('whatsappHub.composer.recipients') }}
            <span v-if="withoutPhone" class="wc-block__warn"><i class="fas fa-phone-slash"/>{{ $t('whatsappHub.actions.excludedNoPhone', {n: withoutPhone}) }}</span>
          </h6>
          <div class="wc-chips">
            <span v-for="r in recipients" :key="r.clientId" class="wc-chip" :class="{'wc-chip--session': r.sessionOpen}"
                  v-b-tooltip.hover :title="(r.phone || '') + (r.sessionOpen ? ' · ' + $t('whatsappHub.table.sessionOpenShort') : '')">
              <i class="wc-chip__dot"/>{{ fullName(r) }}
              <button type="button" class="wc-chip__x" @click="removeRecipient(r)"><i class="fas fa-times"/></button>
            </span>
            <span v-if="recipients.length === 0" class="wc-muted">{{ $t('whatsappHub.composer.noRecipients') }}</span>
          </div>
        </section>

        <!-- mode -->
        <section class="wc-block">
          <h6 class="wc-block__title">{{ $t('whatsappHub.composer.mode') }}</h6>
          <div class="wc-modes">
            <button type="button" class="wc-mode" :class="{'wc-mode--on': mode === 'TEMPLATE'}" @click="mode = 'TEMPLATE'">
              <i class="fas fa-file-lines"/>
              <b>{{ $t('whatsappHub.composer.modeTemplate') }}</b>
              <small>{{ $t('whatsappHub.composer.modeTemplateHint') }}</small>
            </button>
            <button type="button" class="wc-mode" :class="{'wc-mode--on': mode === 'TEXT', 'wc-mode--blocked': notInSession > 0}" @click="mode = 'TEXT'">
              <i class="fas fa-keyboard"/>
              <b>{{ $t('whatsappHub.composer.modeText') }}</b>
              <small v-if="notInSession > 0" class="wc-mode__warn">{{ $t('whatsappHub.composer.notInSession', {n: notInSession}) }}</small>
              <small v-else>{{ $t('whatsappHub.composer.modeTextHint') }}</small>
            </button>
            <button type="button" class="wc-mode" :class="{'wc-mode--on': mode === 'MEDIA', 'wc-mode--blocked': notInSession > 0 || !canMedia}"
                    @click="mode = 'MEDIA'">
              <i class="fas fa-image"/>
              <b>{{ $t('whatsappHub.composer.modeMedia') }}</b>
              <small v-if="!canMedia" class="wc-mode__warn">{{ $t('whatsappHub.templates.twilioOnly') }}</small>
              <small v-else-if="notInSession > 0" class="wc-mode__warn">{{ $t('whatsappHub.composer.notInSession', {n: notInSession}) }}</small>
              <small v-else>{{ $t('whatsappHub.composer.modeMediaHint') }}</small>
            </button>
          </div>
          <div v-if="mode !== 'TEMPLATE' && notInSession > 0" class="wc-session-fix">
            <i class="fas fa-circle-info"/>
            <span>{{ $t('whatsappHub.composer.sessionRule') }}</span>
            <b-button size="sm" variant="outline-warning" @click="keepOnlyInSession">{{ $t('whatsappHub.composer.onlyInSession', {n: recipients.length - notInSession}) }}</b-button>
          </div>
        </section>

        <!-- template picker -->
        <section v-if="mode === 'TEMPLATE'" class="wc-block">
          <h6 class="wc-block__title">{{ $t('whatsappHub.composer.template') }}
            <span class="wc-block__tools">
              <b-form-input v-model="templateSearch" size="sm" :placeholder="$t('whatsappHub.search')" class="wc-tpl-search" debounce="100"/>
              <b-form-select v-model="templateLang" size="sm" :options="languageOptions" class="wc-tpl-lang"/>
              <b-form-checkbox v-model="approvedOnly" switch size="sm" class="wc-toggle">{{ $t('whatsappHub.composer.approvedOnly') }}</b-form-checkbox>
              <button type="button" class="wc-tool" v-b-tooltip.hover :title="$t('whatsappHub.reload')" @click="loadTemplates(true)">
                <i class="fas fa-redo" :class="{'fa-spin': loadingTemplates}"/>
              </button>
            </span>
          </h6>
          <div v-if="loadingTemplates" class="wc-muted py-2"><b-spinner small class="mr-50"/>{{ $t('whatsappHub.composer.loadingTemplates') }}</div>
          <div v-else-if="templatesError" class="wc-error"><i class="fas fa-triangle-exclamation mr-50"/>{{ templatesError }}</div>
          <div v-else-if="filteredTemplates.length === 0" class="wc-muted py-2">{{ $t('whatsappHub.composer.noTemplates') }}</div>
          <div v-else class="wc-tpls">
            <button v-for="tpl in filteredTemplates" :key="tpl.sid" type="button" class="wc-tpl"
                    :class="{'wc-tpl--on': template && template.sid === tpl.sid}" @click="pickTemplate(tpl)">
              <span class="wc-tpl__top">
                <span class="wc-tpl__name">{{ tpl.name }}</span>
                <span class="wc-tpl__status" :data-tone="approvalTone(tpl.approvalStatus)">{{ $t('whatsappHub.templates.statuses.' + (tpl.approvalStatus || 'unsubmitted')) }}</span>
              </span>
              <span class="wc-tpl__meta">
                <span><i class="fas fa-language"/>{{ tpl.language }}</span>
                <span><i :class="typeIcon(tpl.type)"/>{{ $t('whatsappHub.builder.types.' + typeKey(tpl.type)) }}</span>
                <span v-if="tpl.category"><i class="fas fa-folder-open"/>{{ tpl.category }}</span>
              </span>
              <span class="wc-tpl__body">{{ tpl.body || tpl.title || $t('whatsappHub.templates.noBody') }}</span>
            </button>
          </div>

          <!-- variables -->
          <div v-if="template && templateVariables.length" class="wc-vars">
            <h6 class="wc-block__title">{{ $t('whatsappHub.composer.variables') }}</h6>
            <div v-for="key in templateVariables" :key="key" class="wc-var">
              <span class="wc-var__key tabular" v-text="'{{' + key + '}}'"/>
              <div class="wc-var__inputs">
                <b-form-select size="sm" :value="tagValueOf(variables[key])" :options="tagSelectOptions" class="wc-var__select"
                               @change="value => $set(variables, key, value === '' ? '' : value)"/>
                <b-form-input v-model="variables[key]" size="sm" class="wc-var__text"
                              :placeholder="template.variables && template.variables[key] ? template.variables[key] : $t('whatsappHub.composer.customText')"/>
              </div>
            </div>
          </div>
          <div v-if="template && template.approvalStatus && template.approvalStatus !== 'approved'" class="wc-error mt-50">
            <i class="fas fa-triangle-exclamation mr-50"/>{{ $t('whatsappHub.composer.notApproved') }}
          </div>
        </section>

        <!-- free text / media -->
        <section v-if="mode === 'TEXT' || mode === 'MEDIA'" class="wc-block">
          <template v-if="mode === 'MEDIA'">
            <h6 class="wc-block__title">{{ $t('whatsappHub.composer.mediaUrl') }}</h6>
            <b-form-input v-model="mediaUrl" size="sm" placeholder="https://…/welcome.pdf" class="mb-50"/>
            <small class="wc-muted d-block mb-1">{{ $t('whatsappHub.composer.mediaHint') }}</small>
          </template>
          <h6 class="wc-block__title">{{ mode === 'MEDIA' ? $t('whatsappHub.composer.mediaCaption') : $t('whatsappHub.composer.body') }}
            <span class="wc-block__tools">
              <b-form-select v-model="insertPick" size="sm" :options="insertSelectOptions" class="wc-insert-select"
                             @change="value => { if (value) { insertTag(value); $nextTick(() => insertPick = ''); } }"/>
            </span>
          </h6>
          <b-form-textarea ref="bodyInput" v-model="body" rows="5" :placeholder="$t('whatsappHub.composer.bodyPlaceholder')"/>
          <small class="wc-muted">{{ $t('whatsappHub.composer.markdownHint') }}</small>
        </section>

        <!-- schedule -->
        <section class="wc-block wc-block--row">
          <b-form-checkbox v-model="sendLater" switch class="wc-toggle wc-toggle--big">{{ $t('whatsappHub.composer.sendLater') }}</b-form-checkbox>
          <flat-pickr v-if="sendLater" v-model="scheduleAt" class="form-control form-control-sm wc-when"
                      :config="{enableTime: true, time_24hr: true, dateFormat: 'Y-m-d H:i', minDate: 'today', minuteIncrement: 5}"/>
        </section>
      </div>

      <!-- preview column -->
      <aside class="wc-side">
        <div class="wc-side__head">
          <span>{{ $t('whatsappHub.composer.preview') }}</span>
          <b-form-select v-if="recipients.length > 1" v-model="previewIndex" size="sm" class="wc-side__who"
                         :options="recipients.map((r, i) => ({value: i, text: fullName(r)}))"/>
        </div>
        <whatsapp-phone-preview
            :type="previewType" :body="previewBody" :header="template ? template.header : ''" :footer="template ? template.footer : ''"
            :title="template ? template.title : ''" :subtitle="template ? template.subtitle : ''" :button="template ? template.button : ''"
            :media-urls="previewMedia" :actions="template ? template.actions || [] : []" :items="template ? template.items || [] : []"
            :variables="mode === 'TEMPLATE' ? resolvedVariables : null" :tags="previewTags"
            :sender="status ? status.branchName : ''" :recipient-line="previewRecipient ? (previewRecipient.phone || '') : ''"
            :empty-text="$t('whatsappHub.composer.previewEmpty')"/>
        <div v-if="previewLoading" class="wc-muted text-center mt-50"><b-spinner small/></div>
        <p class="wc-side__note"><i class="fas fa-circle-info mr-25"/>{{ $t('whatsappHub.composer.previewFor') }}</p>
      </aside>

      <!-- footer -->
      <div class="wc-foot">
        <span v-if="sendError" class="wc-error mr-auto"><i class="fas fa-triangle-exclamation mr-50"/>{{ sendError }}</span>
        <b-button variant="flat-secondary" :disabled="sending" @click="close">{{ $t('whatsappHub.composer.close') }}</b-button>
        <b-button variant="success" :disabled="!canSend || sending" @click="send">
          <b-spinner v-if="sending" small class="mr-50"/>
          <i v-else :class="sendLater ? 'far fa-clock mr-50' : 'fas fa-paper-plane mr-50'"/>
          {{ sending ? $t('whatsappHub.composer.sending') : sendLater ? $t('whatsappHub.composer.scheduleNow') : $t('whatsappHub.composer.sendNow', {n: recipients.length}) }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import flatPickr from 'vue-flatpickr-component'
import WhatsappPhonePreview from './WhatsappPhonePreview.vue'
import {approvalTone, collectVariables, fullName, replaceTags, TEMPLATE_TYPES, typeKey} from './whatsappUtils'

/**
 * Send one message to one or many guests: an approved template (with
 * per-guest variables), free text or a media attachment inside the 24h
 * session, now or scheduled. Shows a live WhatsApp preview for any recipient
 * and reports the outcome per guest.
 */
export default {
  name: 'WhatsappComposer',
  components: {flatPickr, WhatsappPhonePreview},
  props: {
    buid: {type: [Number, String], default: null},
    status: {type: Object, default: null},
  },
  data: () => ({
    visible: false,
    recipients: [],
    withoutPhone: 0,
    mode: 'TEMPLATE',
    template: null,
    templateSearch: '',
    templateLang: 'all',
    approvedOnly: true,
    loadingTemplates: false,
    templatesError: null,
    variables: {},
    body: '',
    mediaUrl: '',
    insertPick: '',
    sendLater: false,
    scheduleAt: null,
    previewIndex: 0,
    previewTags: null,
    previewLoading: false,
    previewCache: {},
    sending: false,
    sendError: null,
    results: null,
  }),
  computed: {
    ...mapGetters('whatsapp', {templatesOf: 'hubTemplates', tagList: 'hubTags'}),
    templates() {
      return this.templatesOf(this.buid) || [];
    },
    canMedia() {
      return this.status == null || this.status.canSendMedia !== false;
    },
    notInSession() {
      return this.recipients.filter(r => !r.sessionOpen).length;
    },
    languageOptions() {
      const langs = Array.from(new Set(this.templates.map(t => t.language).filter(Boolean)));
      return [{value: 'all', text: this.$t('whatsappHub.composer.allLanguages')}, ...langs.map(l => ({value: l, text: l}))];
    },
    filteredTemplates() {
      const q = (this.templateSearch || '').toLowerCase();
      return this.templates
          .filter(t => !this.approvedOnly || t.approvalStatus === 'approved' || t.provider === 'META')
          .filter(t => this.templateLang === 'all' || t.language === this.templateLang)
          .filter(t => q === '' || [t.name, t.body, t.title, t.category].filter(Boolean).join(' ').toLowerCase().includes(q));
    },
    templateVariables() {
      return this.template ? collectVariables(this.template) : [];
    },
    tagGroups() {
      const groups = [];
      (this.tagList || []).forEach(tag => {
        let group = groups.find(g => g.key === tag.group);
        if (!group) groups.push(group = {key: tag.group, tags: []});
        group.tags.push(tag);
      });
      return groups;
    },
    /** native <select> with option groups — scrolls fine inside the scrolling composer body */
    tagSelectOptions() {
      return [
        {value: '', text: this.$t('whatsappHub.composer.customText')},
        ...this.tagGroups.map(group => ({
          label: this.$t('whatsappHub.tagGroups.' + group.key),
          options: group.tags.map(tag => ({value: tag.token, text: tag.label + '  ' + tag.token})),
        })),
      ];
    },
    insertSelectOptions() {
      return [
        {value: '', text: this.$t('whatsappHub.composer.tagInsert') + '…'},
        ...this.tagGroups.map(group => ({
          label: this.$t('whatsappHub.tagGroups.' + group.key),
          options: group.tags.map(tag => ({value: tag.token, text: tag.label + '  ' + tag.token})),
        })),
      ];
    },
    previewRecipient() {
      return this.recipients[this.previewIndex] || this.recipients[0] || null;
    },
    /** variables with #tags# already replaced for the previewed guest */
    resolvedVariables() {
      const out = {};
      this.templateVariables.forEach(key => {
        const raw = this.variables[key];
        out[key] = raw == null || raw === '' ? '' : replaceTags(raw, this.previewTags);
      });
      return out;
    },
    previewType() {
      if (this.mode === 'TEMPLATE') return this.template ? this.template.type : 'TEXT';
      return this.mode === 'MEDIA' ? 'MEDIA' : 'TEXT';
    },
    previewBody() {
      if (this.mode === 'TEMPLATE') return this.template ? (this.template.body || '') : '';
      return this.body;
    },
    previewMedia() {
      if (this.mode === 'TEMPLATE') return this.template ? (this.template.mediaUrls || []) : [];
      return this.mode === 'MEDIA' && this.mediaUrl ? [this.mediaUrl] : [];
    },
    canSend() {
      if (this.recipients.length === 0) return false;
      if (this.sendLater && !this.scheduleAt) return false;
      if (this.mode === 'TEMPLATE') return this.template != null;
      if (this.notInSession > 0) return false;
      if (this.mode === 'MEDIA') return !!this.mediaUrl && this.canMedia;
      return this.body.trim() !== '';
    },
    okCount() { return (this.results || []).filter(r => r.success && !r.scheduled).length; },
    failCount() { return (this.results || []).filter(r => !r.success).length; },
    scheduledCount() { return (this.results || []).filter(r => r.success && r.scheduled).length; },
  },
  watch: {
    previewRecipient(recipient) {
      this.loadPreviewTags(recipient);
    },
    template(tpl) {
      // sensible defaults: the first variable is almost always the guest's name
      const vars = {};
      (tpl ? collectVariables(tpl) : []).forEach((key, i) => {
        vars[key] = this.variables[key] != null ? this.variables[key] : (i === 0 ? '#firstname#' : '');
      });
      this.variables = vars;
    },
  },
  methods: {
    ...mapActions('whatsapp', ['hubTemplates', 'hubTags', 'hubSend', 'hubPreview']),
    fullName, approvalTone, typeKey,

    /** @param rows audience/conversation rows; @param options {mode, template} */
    open(rows, options) {
      const all = Array.isArray(rows) ? rows : [rows];
      this.recipients = all.filter(r => r && r.phone).map(r => ({
        clientId: r.clientId, contractId: r.contractId, phone: r.phone,
        firstname: r.firstname, lastname: r.lastname, name: fullName(r), sessionOpen: !!r.sessionOpen,
      }));
      this.withoutPhone = all.length - this.recipients.length;
      this.mode = options && options.mode ? options.mode : 'TEMPLATE';
      if (this.mode !== 'TEMPLATE' && this.notInSession > 0) this.mode = 'TEMPLATE';
      this.results = null;
      this.sendError = null;
      this.previewIndex = 0;
      this.visible = true;
      this.hubTags();
      this.loadTemplates(false).then(() => {
        if (options && options.template) this.pickTemplate(options.template);
      });
      this.loadPreviewTags(this.previewRecipient);
    },
    close() {
      this.visible = false;
    },
    reset() {
      this.template = null;
      this.variables = {};
      this.body = '';
      this.mediaUrl = '';
      this.sendLater = false;
      this.scheduleAt = null;
      this.previewCache = {};
      this.previewTags = null;
      if (this.results && this.results.some(r => r.success)) this.$emit('sent', this.results);
      this.results = null;
      this.$emit('closed');
    },
    loadTemplates(force) {
      if (!this.buid) return Promise.resolve();
      this.templatesError = null;
      this.loadingTemplates = true;
      return this.hubTemplates({buid: this.buid, force}).catch(error => {
        this.templatesError = error;
      }).finally(() => this.loadingTemplates = false);
    },
    pickTemplate(tpl) {
      const found = this.templates.find(t => t.sid === tpl.sid) || tpl;
      this.template = found;
      this.mode = 'TEMPLATE';
    },
    typeIcon(twilioType) {
      const found = TEMPLATE_TYPES.find(t => t.twilio === twilioType);
      return found ? found.icon : 'fas fa-align-left';
    },
    /** the select mirrors the input: a known #tag# shows as that option, anything else as "custom text" */
    tagValueOf(value) {
      const v = (value || '').trim();
      return (this.tagList || []).some(tag => tag.token === v) ? v : '';
    },
    removeRecipient(r) {
      this.recipients = this.recipients.filter(x => x.clientId !== r.clientId);
      if (this.previewIndex >= this.recipients.length) this.previewIndex = 0;
    },
    keepOnlyInSession() {
      this.recipients = this.recipients.filter(r => r.sessionOpen);
      this.previewIndex = 0;
    },
    insertTag(token) {
      const el = this.$refs.bodyInput && this.$refs.bodyInput.$el;
      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart, end = el.selectionEnd;
        this.body = this.body.substring(0, start) + token + this.body.substring(end);
        this.$nextTick(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
      } else {
        this.body += token;
      }
    },
    loadPreviewTags(recipient) {
      if (!recipient) { this.previewTags = null; return; }
      const key = recipient.clientId + ':' + (recipient.contractId || '');
      if (this.previewCache[key]) { this.previewTags = this.previewCache[key]; return; }
      this.previewLoading = true;
      this.hubPreview({clientId: recipient.clientId, contractId: recipient.contractId}).then(tags => {
        this.$set(this.previewCache, key, tags);
        if (this.previewRecipient && this.previewRecipient.clientId === recipient.clientId) this.previewTags = tags;
      }).catch(() => {
        this.previewTags = {firstname: recipient.firstname || '', lastname: recipient.lastname || '', fullname: recipient.name || ''};
      }).finally(() => this.previewLoading = false);
    },
    send() {
      if (!this.canSend) return;
      this.sending = true;
      this.sendError = null;
      const request = {
        buid: this.buid,
        recipients: this.recipients.map(r => ({clientId: r.clientId, contractId: r.contractId})),
        mode: this.mode,
        body: this.mode === 'TEMPLATE' ? (this.template.body || null) : this.body,
        mediaUrl: this.mode === 'MEDIA' ? this.mediaUrl : null,
        contentSid: this.mode === 'TEMPLATE' && this.template.provider !== 'META' ? this.template.sid : null,
        templateName: this.mode === 'TEMPLATE' ? this.template.name : null,
        templateLanguage: this.mode === 'TEMPLATE' ? this.template.language : null,
        variables: this.mode === 'TEMPLATE' ? this.variables : null,
        scheduleAt: this.sendLater && this.scheduleAt ? this.$moment(this.scheduleAt, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DDTHH:mm:ss') : null,
      };
      this.hubSend(request).then(ans => {
        this.results = ans.results || [];
      }).catch(error => {
        this.sendError = error;
      }).finally(() => this.sending = false);
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wc-title { display: inline-flex; align-items: center; gap: 8px; font-weight: 800; i { color: #25d366; font-size: 1.1rem; } }
.wc-title__count { background: rgba(37, 211, 102, .16); color: #1f9d57; font-size: .72rem; font-weight: 800; padding: 2px 8px; border-radius: 999px; }

.wc { display: grid; grid-template-columns: minmax(0, 1fr) 320px; grid-template-rows: 1fr auto; }
.wc-main { padding: 16px 18px; display: flex; flex-direction: column; gap: 16px; max-height: 70vh; overflow-y: auto; }
.wc-side { grid-row: 1; grid-column: 2; padding: 16px 18px; background: rgba(15, 23, 42, .03); border-left: 1px solid rgba(15, 23, 42, .06); display: flex; flex-direction: column; gap: 10px; }
.wc-side__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: .68rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; }
.wc-side__who { max-width: 170px; }
.wc-side__note { margin: 0; font-size: .68rem; color: #94a3b8; }
.wc-foot { grid-column: 1 / -1; display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid rgba(15, 23, 42, .06); }

.wc-block { display: flex; flex-direction: column; gap: 8px; &--row { flex-direction: row; align-items: center; gap: 12px; } }
.wc-block__title { display: flex; align-items: center; gap: 10px; margin: 0; font-size: .68rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; }
.wc-block__warn { font-size: .66rem; text-transform: none; letter-spacing: 0; color: #ea5455; i { margin-right: 4px; } }
.wc-block__tools { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; text-transform: none; letter-spacing: 0; }
.wc-tpl-search { width: 160px; }
.wc-tpl-lang { width: 90px; }
.wc-toggle { margin: 0; font-size: .72rem; font-weight: 700; color: #64748b; white-space: nowrap; &--big { font-size: .82rem; } :deep(.custom-control-label) { cursor: pointer; } }
.wc-tool {
  width: 28px; height: 28px; border: none; border-radius: 8px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, .05); color: #64748b; font-size: .7rem;
  &:focus { outline: none; }
}
.wc-muted { color: #94a3b8; font-size: .76rem; }
.wc-error { color: #ea5455; font-size: .78rem; font-weight: 600; }
.wc-when { width: 170px; }

.wc-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.wc-chip {
  display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 4px 6px 4px 10px;
  background: rgba(15, 23, 42, .05); color: #1f2a44; font-size: .74rem; font-weight: 700; text-transform: capitalize;
  &--session .wc-chip__dot { background: #25d366; box-shadow: 0 0 0 3px rgba(37, 211, 102, .2); }
}
.wc-chip__dot { width: 6px; height: 6px; border-radius: 50%; background: #ff9f43; }
.wc-chip__x { border: none; background: none; padding: 0 4px; color: #94a3b8; cursor: pointer; font-size: .66rem; &:hover { color: #ea5455; } &:focus { outline: none; } }

.wc-modes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.wc-mode {
  display: flex; flex-direction: column; align-items: flex-start; gap: 2px; text-align: left;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 12px; background: #fff; padding: 10px 12px; cursor: pointer; transition: all .15s;
  i { font-size: 1rem; color: #94a3b8; margin-bottom: 4px; }
  b { font-size: .82rem; color: #1f2a44; }
  small { font-size: .66rem; color: #94a3b8; line-height: 1.3; }
  &:hover { border-color: rgba(37, 211, 102, .5); }
  &:focus { outline: none; }
  &--on { border-color: #25d366; background: rgba(37, 211, 102, .08); i { color: #1f9d57; } }
  &--blocked { opacity: .75; }
}
.wc-mode__warn { color: #c77400 !important; font-weight: 700; }
.wc-session-fix { display: flex; align-items: center; gap: 10px; font-size: .76rem; color: #c77400; background: rgba(255, 159, 67, .1); border-radius: 10px; padding: 8px 12px; }

.wc-tpls { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; max-height: 260px; overflow-y: auto; padding: 2px; }
.wc-tpl {
  display: flex; flex-direction: column; gap: 4px; text-align: left;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 12px; background: #fff; padding: 10px 12px; cursor: pointer; transition: all .15s;
  &:hover { border-color: rgba(37, 211, 102, .5); }
  &:focus { outline: none; }
  &--on { border-color: #25d366; background: rgba(37, 211, 102, .08); box-shadow: 0 0 0 2px rgba(37, 211, 102, .2); }
}
.wc-tpl__top { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.wc-tpl__name { font-size: .8rem; font-weight: 800; color: #1f2a44; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wc-tpl__status {
  font-size: .58rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; padding: 2px 6px; border-radius: 999px; white-space: nowrap;
  &[data-tone="ok"] { background: rgba(40, 199, 111, .14); color: #1f9d57; }
  &[data-tone="wait"] { background: rgba(255, 159, 67, .14); color: #c77400; }
  &[data-tone="bad"] { background: rgba(234, 84, 85, .14); color: #ea5455; }
  &[data-tone="muted"] { background: rgba(100, 116, 139, .12); color: #64748b; }
}
.wc-tpl__meta { display: flex; flex-wrap: wrap; gap: 8px; font-size: .64rem; color: #94a3b8; i { margin-right: 3px; } }
.wc-tpl__body { font-size: .72rem; color: #475569; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; white-space: pre-wrap; }

.wc-vars { display: flex; flex-direction: column; gap: 6px; margin-top: 4px; }
.wc-var { display: grid; grid-template-columns: 64px 1fr; align-items: center; gap: 8px; }
.wc-var__key { font-size: .72rem; font-weight: 800; color: #7367f0; }
.wc-var__inputs { display: grid; grid-template-columns: minmax(180px, 220px) 1fr; gap: 6px; }
.wc-insert-select { width: 220px; }
@media (max-width: 576px) { .wc-var__inputs { grid-template-columns: 1fr; } }

.wc-results { padding: 16px 18px 0; }
.wc-results__head { display: flex; gap: 16px; font-size: .84rem; font-weight: 800; margin-bottom: 10px; i { margin-right: 6px; } }
.wc-results__ok { color: #1f9d57; }
.wc-results__fail { color: #ea5455; }
.wc-results__sched { color: #7367f0; }
.wc-results__list { max-height: 55vh; overflow-y: auto; display: flex; flex-direction: column; }
.wc-result {
  display: grid; grid-template-columns: 20px 180px 130px 1fr; gap: 10px; align-items: center;
  padding: 7px 8px; border-radius: 8px; font-size: .76rem; border-bottom: 1px solid rgba(15, 23, 42, .05);
  &--ok i { color: #1f9d57; }
  &--fail { background: rgba(234, 84, 85, .06); i { color: #ea5455; } .wc-result__text { color: #ea5455; font-weight: 600; } }
}
.wc-result__name { font-weight: 700; color: #1f2a44; text-transform: capitalize; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wc-result__phone { color: #94a3b8; }
.wc-result__text { color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wc-results .wc-foot { margin: 10px -18px 0; }

@media (max-width: 992px) {
  .wc { grid-template-columns: 1fr; }
  .wc-side { grid-row: auto; grid-column: 1; border-left: none; border-top: 1px solid rgba(15, 23, 42, .06); }
  .wc-modes { grid-template-columns: 1fr; }
  .wc-result { grid-template-columns: 20px 1fr; .wc-result__phone, .wc-result__text { grid-column: 2; } }
}
</style>

<style lang="scss">
.wc-modal .modal-body { overflow: visible; }
.dark-layout {
  .wc-side { background: rgba(255, 255, 255, .03); border-color: rgba(255, 255, 255, .08); }
  .wc-foot { border-color: rgba(255, 255, 255, .08); }
  .wc-chip { background: rgba(255, 255, 255, .08); color: #e9ecf2; }
  .wc-mode, .wc-tpl { background: #283046; border-color: rgba(255, 255, 255, .12); b { color: #e9ecf2; } }
  .wc-mode--on, .wc-tpl--on { background: rgba(37, 211, 102, .12); border-color: #25d366; }
  .wc-tpl__name { color: #e9ecf2; }
  .wc-tpl__body { color: #b4b7bd; }
  .wc-tool { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .wc-result { border-color: rgba(255, 255, 255, .06); }
  .wc-result__name { color: #e9ecf2; }
  .wc-toggle { color: #94a3b8; }
}
</style>
